import { StrictMode } from 'react';
import { createRoot } from 'react-dom/client';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { BrowserRouter } from 'react-router-dom';
import './i18n';
import './styles.css';
import { App } from './App';
import { loadToken, setToken } from './api/token';
import { MOCK, ApiError } from './api/client';
import { useUi } from './store';
import { CODAI_PENDING_KEY } from './api/token';

function applyTheme() {
  try {
    const th = localStorage.getItem('pk.theme');
    if (th === 'light' || th === 'dark') document.documentElement.dataset.theme = th;
  } catch { /* ignore */ }
}

async function boot() {
  applyTheme();
  await loadToken();
  // Return from "Continue with Codai": ...#token=<jwt>. Only accepted right after a Codai redirect that THIS tab
  // started (flag set in Auth.tsx before leaving); a #token= planted in a link someone sends us is ignored.
  let codaiPending = false;
  try {
    codaiPending = sessionStorage.getItem(CODAI_PENDING_KEY) === '1';
    sessionStorage.removeItem(CODAI_PENDING_KEY);
  } catch { /* storage unavailable: Codai login cannot complete */ }
  const m = location.hash.match(/token=([^&]+)/);
  if (m) {
    if (codaiPending) await setToken(decodeURIComponent(m[1]));
    history.replaceState(null, '', location.pathname + location.search);
  }
  // Failed Codai login: ...#error=<message>
  const e = location.hash.match(/error=([^&]+)/);
  if (e) {
    useUi.getState().pushToast('error', decodeURIComponent(e[1]!.replace(/\+/g, ' ')));
    history.replaceState(null, '', location.pathname + location.search);
  }
  if (MOCK && !new URLSearchParams(location.search).has('mockauth')) {
    await setToken('mock-token');
  }
  const client = new QueryClient({
    defaultOptions: {
      queries: {
        retry: (n, err) => !(err instanceof ApiError && err.status >= 400 && err.status < 500) && n < 2,
        refetchOnWindowFocus: true,
        staleTime: 10_000,
      },
    },
  });
  createRoot(document.getElementById('root')!).render(
    <StrictMode>
      <QueryClientProvider client={client}>
        <BrowserRouter>
          <App />
        </BrowserRouter>
      </QueryClientProvider>
    </StrictMode>,
  );
}

void boot();
